<!--
  A free-text, multi-value tag field offering existing tags ordered most-used-first
  (`useEntryTags`, itself just `tags_list` sorted server-side) - so retyping "groceries"
  finds the one already in use instead of inventing "Groceries" beside it. Typing a name
  no suggestion matches is still accepted: the tag set only ever grows from what someone
  actually types on an entry (AGENTS.md's captain note on this task).
-->
<script setup lang="ts">
import { computed, onUnmounted, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import type { TagOption } from '../internal/tag-options.ts'

const props = defineProps<{
  id: string
  label: string
  modelValue: readonly string[]
  suggestions: readonly TagOption[]
}>()

const emit = defineEmits<{ 'update:modelValue': [readonly string[]] }>()

const { t } = useI18n()

const inputRef = useTemplateRef<HTMLInputElement>('input')
const query = ref('')
const isOpen = ref(false)
const highlightIndex = ref(0)
const popoverStyle = ref<{ top: string; left: string; width: string }>({
  top: '0px',
  left: '0px',
  width: '0px',
})

// Same reasoning as AccountCombobox.vue: the grid's horizontal-scroll wrapper clips an
// in-place popover, so this one is teleported and positioned from the input's own rect.
function updatePopoverPosition(): void {
  const rect = inputRef.value?.getBoundingClientRect()
  if (rect === undefined) return
  popoverStyle.value = {
    top: `${rect.bottom + 2}px`,
    left: `${rect.left}px`,
    width: `${Math.max(rect.width, 240)}px`,
  }
}

function onReposition(): void {
  if (isOpen.value) updatePopoverPosition()
}
window.addEventListener('scroll', onReposition, true)
window.addEventListener('resize', onReposition)
onUnmounted(() => {
  window.removeEventListener('scroll', onReposition, true)
  window.removeEventListener('resize', onReposition)
})

const selected = computed(() => new Set(props.modelValue))
const matches = computed(() => {
  const needle = query.value.trim().toLowerCase()
  return props.suggestions.filter(
    (tag) => !selected.value.has(tag.tag) && (needle === '' || tag.tag.toLowerCase().includes(needle)),
  )
})

function optionId(tag: string): string {
  return `${props.id}-option-${tag}`
}

function openList(): void {
  updatePopoverPosition()
  isOpen.value = true
  highlightIndex.value = 0
}

function closeList(): void {
  isOpen.value = false
}

function addTag(tag: string): void {
  const trimmed = tag.trim()
  if (trimmed === '' || trimmed.length > 50 || selected.value.has(trimmed)) return
  emit('update:modelValue', [...props.modelValue, trimmed])
  query.value = ''
}

function removeTag(tag: string): void {
  emit(
    'update:modelValue',
    props.modelValue.filter((candidate) => candidate !== tag),
  )
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    if (!isOpen.value) return openList()
    highlightIndex.value = Math.min(highlightIndex.value + 1, matches.value.length - 1)
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    highlightIndex.value = Math.max(highlightIndex.value - 1, 0)
  } else if (event.key === 'Enter' || event.key === ',') {
    const highlighted = matches.value[highlightIndex.value]?.tag
    if (highlighted !== undefined && isOpen.value && query.value.trim() !== '') {
      event.preventDefault()
      addTag(highlighted)
    } else if (query.value.trim() !== '') {
      event.preventDefault()
      addTag(query.value)
    }
  } else if (event.key === 'Backspace' && query.value === '' && props.modelValue.length > 0) {
    removeTag(props.modelValue[props.modelValue.length - 1]!)
  } else if (event.key === 'Escape') {
    closeList()
  }
}

function onBlur(): void {
  closeList()
}

defineExpose({ focus: () => inputRef.value?.focus() })
</script>

<template>
  <div
    class="relative flex min-h-[var(--density-control)] flex-wrap items-center gap-[var(--space-tight)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-tight)] py-[var(--space-hair)]"
  >
    <span
      v-for="tag in modelValue"
      :key="tag"
      class="flex items-center gap-[var(--space-hair)] rounded-[var(--radius-control)] bg-[color:var(--color-surface-raised)] px-[var(--space-tight)] py-[var(--space-hair)] text-[length:var(--text-step-1)] text-[color:var(--color-text)]"
    >
      {{ tag }}
      <button
        type="button"
        :aria-label="t('entries.removeTag', { tag })"
        class="text-[color:var(--color-text-muted)] hover:text-[color:var(--color-danger)]"
        @click="removeTag(tag)"
      >
        ×
      </button>
    </span>
    <input
      :id="id"
      ref="input"
      v-model="query"
      type="text"
      role="combobox"
      autocomplete="off"
      :aria-label="label"
      aria-autocomplete="list"
      :aria-expanded="isOpen"
      :aria-controls="`${id}-listbox`"
      :aria-activedescendant="
        isOpen && matches[highlightIndex] ? optionId(matches[highlightIndex]!.tag) : undefined
      "
      class="min-w-24 flex-1 border-0 bg-transparent text-[length:var(--text-step-3)] text-[color:var(--color-text)] outline-none"
      @input="openList"
      @focus="openList"
      @blur="onBlur"
      @keydown="onKeydown"
    />
    <Teleport to="body">
      <ul
        v-if="isOpen && matches.length > 0"
        :id="`${id}-listbox`"
        role="listbox"
        :aria-label="label"
        class="fixed z-20 max-h-72 overflow-y-auto rounded-[var(--radius-overlay)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] py-[var(--space-tight)] shadow-[var(--shadow-overlay)]"
        :style="popoverStyle"
      >
        <li
          v-for="(tag, index) in matches"
          :id="optionId(tag.tag)"
          :key="tag.tag"
          role="option"
          :aria-selected="index === highlightIndex"
          class="flex cursor-pointer items-baseline justify-between gap-[var(--space-cell)] px-[var(--space-cell)] py-[var(--space-inline)]"
          :class="index === highlightIndex ? 'bg-[color:var(--color-surface-raised)]' : undefined"
          @mousedown.prevent="addTag(tag.tag)"
          @mouseenter="highlightIndex = index"
        >
          <span class="min-w-0 truncate text-[color:var(--color-text)]">{{ tag.tag }}</span>
          <span
            class="shrink-0 whitespace-nowrap text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
          >
            {{ t('entries.tagUsageCount', { count: tag.usageCount }) }}
          </span>
        </li>
      </ul>
    </Teleport>
  </div>
</template>
